import posthog from 'posthog-js';

const POSTHOG_API_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

let isInitialized = false;
let initSkipped = false;
let pendingIdentify:
  | {
      userId: string;
      traits: Record<string, any>;
    }
  | null = null;

type QueuedPosthogEvent = {
  eventName: string;
  properties?: Record<string, unknown>;
};

// Captures from the first page effect run before `_app` calls initPosthog.
// Dropping them there also blocked later retries once callers had marked the
// event as already sent.
let queuedEvents: QueuedPosthogEvent[] = [];

function flushQueuedEvents(): void {
  if (!isInitialized || queuedEvents.length === 0) {
    return;
  }
  const pending = queuedEvents;
  queuedEvents = [];
  for (const event of pending) {
    posthog.capture(event.eventName, event.properties);
  }
}

export const initPosthog = () => {
  if (isInitialized || initSkipped || typeof window === 'undefined') {
    return;
  }

  if (!POSTHOG_API_KEY) {
    initSkipped = true;
    queuedEvents = [];
    if (process.env.NODE_ENV === 'development') {
      console.info('[Posthog] NEXT_PUBLIC_POSTHOG_KEY not set. Skipping init.');
    }
    return;
  }

  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    autocapture: false,
    capture_performance: false,
    persistence: 'localStorage+cookie',
    person_profiles: 'identified_only',
  });

  isInitialized = true;

  if (pendingIdentify) {
    posthog.identify(pendingIdentify.userId, pendingIdentify.traits);
    pendingIdentify = null;
  }

  // Flush after identify so queued events join the person profile.
  // person_profiles is `identified_only`: events captured before identify
  // are stored without a person and never show up in person funnels.
  flushQueuedEvents();
};

export const capturePosthogEvent = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  if (typeof window === 'undefined' || initSkipped) {
    return;
  }
  if (!isInitialized) {
    queuedEvents.push({ eventName, properties });
    return;
  }
  posthog.capture(eventName, properties);
};

/**
 * PostHog feature flag `editor-onboarding-tour`: when explicitly false, skip the tour.
 * When true or flag missing / PostHog not ready, allow the tour (other gates still apply).
 */
export const isEditorTourFeatureEnabled = (): boolean => {
  if (typeof window === 'undefined' || !isInitialized) {
    return true;
  }
  const enabled = posthog.isFeatureEnabled('editor-onboarding-tour');
  if (enabled === false) {
    return false;
  }
  return true;
};

export const identifyPosthogUser = (
  userId: string,
  traits: Record<string, any> = {}
) => {
  if (!userId) {
    return;
  }

  if (!isInitialized) {
    pendingIdentify = { userId, traits };
    return;
  }

  posthog.identify(userId, traits);
};

/** Update PostHog person properties for the currently identified user. */
export const setPosthogPersonProperties = (
  traits: Record<string, any>
) => {
  if (!isInitialized || !traits || Object.keys(traits).length === 0) {
    return;
  }
  posthog.people.set(traits);
};

export const resetPosthog = () => {
  queuedEvents = [];
  pendingIdentify = null;
  if (!isInitialized) {
    return;
  }

  posthog.reset();
};

export default posthog;

